import React from 'react';
import { RadarTargetInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import { Separator } from '@ais-ui/ui-core';
import { LastOriginDisplay } from './LastOriginDisplay';

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

interface IdentificationProps {
  radarInfo: RadarTargetInfo;
}

const Identification = ({ radarInfo }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <IdentificationItem
        label={messages.shipDetailsBaseNameLabel}
        value={radarInfo.name}
      />
      <IdentificationItem label="ID" value={radarInfo.id} />
      <IdentificationItem
        label={messages.shipDetailsBaseMMSILabel}
        value={radarInfo.mmsi}
      />
      <IdentificationItem
        label={messages.reportsDetailsTypeLabel}
        value={radarInfo.targetType}
      />
    </div>
  );
};

interface NavigationDataProps {
  radarInfo: RadarTargetInfo;
}

interface NavigationDataItemProps {
  label: string;
  value: string;
}

const NavigationDataItem = ({ label, value }: NavigationDataItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const NavigationData = ({ radarInfo }: NavigationDataProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <NavigationDataItem
        label={messages.shipDetailsBaseSOGLabel}
        value={radarInfo.sog}
      />
      <NavigationDataItem
        label={messages.shipDetailsBaseCOGLabel}
        value={radarInfo.cog}
      />
    </div>
  );
};

interface ReportingStatusProps {
  radarInfo: RadarTargetInfo;
}

interface ReportingStatusItemProps {
  label: string;
  value: string;
}

const ReportingStatusItem = ({ label, value }: ReportingStatusItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

const ReportingStatus = ({ radarInfo }: ReportingStatusProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <ReportingStatusItem
        label="Last Radar Report"
        value={formatDateTimeWithTimeZone(radarInfo.lastRadarReport)}
      />
      <ReportingStatusItem
        label={messages.shipDetailsBaseLatLabel}
        value={radarInfo.latitude?.toString()}
      />
      <ReportingStatusItem
        label={messages.shipDetailsBaseLonLabel}
        value={radarInfo.longitude?.toString()}
      />
    </div>
  );
};

export const RadarDetailsPanel = ({
  radarInfo,
}: {
  radarInfo: RadarTargetInfo;
}) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            Identification and Basic Details
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification radarInfo={radarInfo}></Identification>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Navigation Data</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <NavigationData radarInfo={radarInfo}></NavigationData>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Reporting Status</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <ReportingStatus radarInfo={radarInfo}></ReportingStatus>
      </div>

      {radarInfo.lastOrigin && (
        <LastOriginDisplay lastOrigin={radarInfo.lastOrigin} />
      )}
    </div>
  );
};
